import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Stack,
  Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { listDevelopers, createCluster, updateCluster, archiveCluster, isLegacyCompany, type PlatformDeveloper } from '../../lib/platform-api';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';

interface ClusterRow {
  id: string;
  name: string;
  slug: string;
  developerName: string;
  developer_id: string;
  address: string | null;
  city: string | null;
  province: string | null;
  postal_code: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  addr_street_enabled: boolean;
  addr_block_enabled: boolean;
  property_type: PropertyType;
}

type PropertyType = 'HOUSING' | 'APARTMENT';

function propertyTypeLabelKey(pt: PropertyType) {
  return pt === 'APARTMENT' ? 'plt.propertyTypeApartment' : 'plt.propertyTypeHousing';
}

export function PlatformClustersPage() {
  const t = useT();
  const location = useLocation();
  const [devs, setDevs] = useState<PlatformDeveloper[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newCluster, setNewCluster] = useState(false);
  const [editing, setEditing] = useState<ClusterRow | null>(null);
  const [archiving, setArchiving] = useState<{ id: string; name: string } | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      setDevs(await listDevelopers());
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    const focus = new URLSearchParams(location.search).get('focus');
    if (focus && devs.some((developer) => developer.clusters.some((cluster) => cluster.id === focus))) {
      document.getElementById(`platform-cluster-${focus}`)?.scrollIntoView({ block: 'center' });
    }
  }, [devs, location.search]);

  // Only active (non-archived) developers; archiving a developer cascades archived_at
  // onto its clusters, so archived developers have no active clusters to show. The API
  // already returns developers and clusters sorted A→Z by name.
  const activeDevs = useMemo(() => devs.filter((d) => !d.archived_at), [devs]);
  const realCompanies = useMemo(() => activeDevs.filter((d) => !isLegacyCompany(d.id)), [activeDevs]);
  const legacyCompany = activeDevs.find((d) => isLegacyCompany(d.id));
  const visibleGroups = [...realCompanies,
    ...(legacyCompany?.clusters.some((cluster) => !cluster.archived_at) ? [legacyCompany] : [])];

  async function confirmArchive() {
    if (!archiving) return;
    setArchiveBusy(true);
    setArchiveError(null);
    try {
      await archiveCluster(archiving.id);
      setArchiving(null);
      refresh();
    } catch (e) {
      setArchiveError(extractApiError(e).message);
    } finally {
      setArchiveBusy(false);
    }
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box sx={{ maxWidth: 640 }}>
          <Typography variant="h4" fontWeight={600}>{t('plt.clusterTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('plt.clusterSubtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNewCluster(true)}
          disabled={realCompanies.length === 0}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {t('plt.newCluster')}
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : visibleGroups.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {t('plt.noDevFirst')}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        visibleGroups.map((dev) => (
          <DeveloperClusterCard
            key={dev.id}
            dev={dev}
            onEdit={setEditing}
            onArchive={(c) => { setArchiveError(null); setArchiving(c); }}
          />
        ))
      )}

      {!loading && realCompanies.length === 0 && visibleGroups.length > 0
        ? <Alert severity="info">{t('plt.noDevFirst')}</Alert> : null}

      <CreateClusterDialog
        open={newCluster}
        developers={realCompanies}
        onClose={() => setNewCluster(false)}
        onCreated={() => { setNewCluster(false); refresh(); }}
      />

      <EditClusterDialog
        cluster={editing}
        developers={activeDevs}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); refresh(); }}
      />

      {/* Archive confirm dialog */}
      <Dialog open={Boolean(archiving)} onClose={() => !archiveBusy && setArchiving(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('plt.archiveClusterTitle', { name: archiving?.name })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {t('plt.archiveClusterWarning')}
            </Typography>
            {archiveError ? <Alert severity="error">{archiveError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiving(null)} disabled={archiveBusy}>{t('common.cancel')}</Button>
          <Button onClick={confirmArchive} variant="contained" color="error" disabled={archiveBusy}>
            {t('plt.archive')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function DeveloperClusterCard({ dev, onEdit, onArchive }: {
  dev: PlatformDeveloper;
  onEdit: (row: ClusterRow) => void;
  onArchive: (c: { id: string; name: string }) => void;
}) {
  const t = useT();
  const clusters = dev.clusters.filter((cl) => !cl.archived_at);
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: clusters.length ? 1.5 : 0 }}>
          <Typography variant="h6">{isLegacyCompany(dev.id) ? t('plt.legacyName') : dev.name}</Typography>
          {isLegacyCompany(dev.id) ? <Chip size="small" color="warning" label={t('plt.legacyBadge')} /> : null}
          <Chip size="small" label={t('plt.clusterCount', { count: clusters.length })} />
          {!isLegacyCompany(dev.id) ? <Typography variant="caption" color="text.secondary">/{dev.slug}</Typography> : null}
        </Stack>
        {isLegacyCompany(dev.id) ? <Alert severity="warning" sx={{ mb: 1.5 }}>{t('plt.legacyHelpInClusters')}</Alert> : null}
        {clusters.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t('plt.noClusterYet')}</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('plt.colCluster')}</TableCell>
                  <TableCell>{t('plt.colType')}</TableCell>
                  <TableCell>{t('plt.colLocation')}</TableCell>
                  <TableCell sx={{ width: 220 }}>{t('plt.colSlug')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clusters.map((cl) => {
                  const row: ClusterRow = {
                    id: cl.id, name: cl.name, slug: cl.slug, developerName: dev.name, developer_id: dev.id,
                    address: cl.address, city: cl.city, province: cl.province,
                    postal_code: cl.postal_code, contact_phone: cl.contact_phone, contact_email: cl.contact_email,
                    addr_street_enabled: cl.addr_street_enabled, addr_block_enabled: cl.addr_block_enabled,
                    property_type: cl.property_type,
                  };
                  return (
                    <TableRow key={cl.id} id={`platform-cluster-${cl.id}`} hover>
                      <TableCell>{cl.name}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t(propertyTypeLabelKey(cl.property_type))}
                        />
                      </TableCell>
                      <TableCell>{[cl.city, cl.province].filter(Boolean).join(', ') || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">/{cl.slug}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('plt.tooltipEditContact')}>
                          <IconButton size="small" onClick={() => onEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('plt.archive')}>
                          <IconButton size="small" color="error" onClick={() => onArchive({ id: cl.id, name: cl.name })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

// Per-cluster unit-address scheme toggles. Number is always required (no toggle);
// Street and Block are independently switchable.
function AddressSchemeFields({ street, block, onStreet, onBlock }: {
  street: boolean; block: boolean; onStreet: (v: boolean) => void; onBlock: (v: boolean) => void;
}) {
  const t = useT();
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">{t('plt.addrSchemeTitle')}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {t('plt.addrSchemeHelper')}
      </Typography>
      <Stack>
        <FormControlLabel control={<Switch checked={street} onChange={(e) => onStreet(e.target.checked)} />} label={t('plt.addrStreet')} />
        <FormControlLabel control={<Switch checked={block} onChange={(e) => onBlock(e.target.checked)} />} label={t('plt.addrBlock')} />
      </Stack>
    </Box>
  );
}

function CreateClusterDialog({ open, developers, onClose, onCreated }: {
  open: boolean; developers: PlatformDeveloper[]; onClose: () => void; onCreated: () => void;
}) {
  const t = useT();
  const [developerId, setDeveloperId] = useState('');
  const [clusterName, setClusterName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [addrStreet, setAddrStreet] = useState(false);
  const [addrBlock, setAddrBlock] = useState(true);
  const [propertyType, setPropertyType] = useState<PropertyType>('HOUSING');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDeveloperId(developers[0]?.id ?? '');
      setClusterName('');
      setAddress('');
      setCity('');
      setProvince('');
      setPostalCode('');
      setContactPhone('');
      setContactEmail('');
      setAddrStreet(false);
      setAddrBlock(true);
      setPropertyType('HOUSING');
      setError(null);
    }
  }, [open, developers]);

  const canSave = developerId && clusterName.trim();

  async function save() {
    setBusy(true); setError(null);
    try {
      await createCluster({
        developer_id: developerId, cluster_name: clusterName.trim(),
        address: address.trim(),
        city: city.trim(),
        province: province.trim(),
        postal_code: postalCode.trim(),
        contact_phone: contactPhone.trim(),
        contact_email: contactEmail.trim(),
        addr_street_enabled: addrStreet,
        addr_block_enabled: addrBlock,
        property_type: propertyType,
      });
      onCreated();
    } catch (e) { setError(extractApiError(e).message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('plt.newClusterTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField select label={t('plt.devField')} value={developerId} onChange={(e) => setDeveloperId(e.target.value)} required fullWidth>
            {developers.map((d) => (<MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>))}
          </TextField>
          <TextField label={t('plt.fieldClusterName')} value={clusterName} onChange={(e) => setClusterName(e.target.value)} required fullWidth />
          <TextField
            select
            label={t('plt.fieldPropertyType')}
            value={propertyType}
            onChange={(e) => setPropertyType(e.target.value as PropertyType)}
            fullWidth
          >
            <MenuItem value="HOUSING">{t('plt.propertyTypeHousing')}</MenuItem>
            <MenuItem value="APARTMENT">{t('plt.propertyTypeApartment')}</MenuItem>
          </TextField>
          <Typography variant="overline" color="text.secondary">{t('plt.clusterDetailsOpt')}</Typography>
          <TextField label={t('plt.fieldAddress')} value={address} onChange={(e) => setAddress(e.target.value)} fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label={t('plt.fieldCity')} value={city} onChange={(e) => setCity(e.target.value)} fullWidth />
            <TextField label={t('plt.fieldProvince')} value={province} onChange={(e) => setProvince(e.target.value)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label={t('plt.fieldPostalCode')} value={postalCode} onChange={(e) => setPostalCode(e.target.value)} fullWidth />
            <TextField label={t('plt.fieldPhone')} value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} fullWidth />
          </Stack>
          <TextField label={t('plt.fieldContactEmail')} type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} fullWidth />
          <AddressSchemeFields street={addrStreet} block={addrBlock} onStreet={setAddrStreet} onBlock={setAddrBlock} />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button onClick={save} variant="contained" disabled={!canSave || busy}>{t('common.create')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function EditClusterDialog({ cluster, developers, onClose, onSaved }: {
  cluster: ClusterRow | null; developers: PlatformDeveloper[]; onClose: () => void; onSaved: () => void;
}) {
  const t = useT();
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [name, setName] = useState('');
  const [developerId, setDeveloperId] = useState('');
  const [addrStreet, setAddrStreet] = useState(false);
  const [addrBlock, setAddrBlock] = useState(true);
  const [propertyType, setPropertyType] = useState<PropertyType>('HOUSING');
  const [showMore, setShowMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cluster) {
      setAddress(cluster.address ?? '');
      setCity(cluster.city ?? '');
      setProvince(cluster.province ?? '');
      setPostalCode(cluster.postal_code ?? '');
      setContactPhone(cluster.contact_phone ?? '');
      setContactEmail(cluster.contact_email ?? '');
      setName(cluster.name);
      setDeveloperId(cluster.developer_id);
      setAddrStreet(cluster.addr_street_enabled);
      setAddrBlock(cluster.addr_block_enabled);
      setPropertyType(cluster.property_type);
      setShowMore(false);
      setError(null);
    }
  }, [cluster]);

  const canSave = Boolean(name.trim());

  async function save() {
    if (!cluster) return;
    setBusy(true); setError(null);
    try {
      await updateCluster(cluster.id, {
        cluster_name: name.trim(),
        developer_id: developerId,
        address: address.trim(),
        city: city.trim(),
        province: province.trim(),
        postal_code: postalCode.trim(),
        contact_phone: contactPhone.trim(),
        contact_email: contactEmail.trim(),
        addr_street_enabled: addrStreet,
        addr_block_enabled: addrBlock,
        property_type: propertyType,
      });
      onSaved();
    } catch (e) { setError(extractApiError(e).message); } finally { setBusy(false); }
  }

  return (
    <Dialog open={Boolean(cluster)} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{t('plt.editClusterTitle', { name: cluster?.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            select
            label={t('plt.fieldPropertyType')}
            value={propertyType}
            onChange={(e) => setPropertyType(e.target.value as PropertyType)}
            fullWidth
          >
            <MenuItem value="HOUSING">{t('plt.propertyTypeHousing')}</MenuItem>
            <MenuItem value="APARTMENT">{t('plt.propertyTypeApartment')}</MenuItem>
          </TextField>
          <Typography variant="overline" color="text.secondary">{t('plt.contactSection')}</Typography>
          <TextField label={t('plt.fieldAddress')} value={address} onChange={(e) => setAddress(e.target.value)} fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label={t('plt.fieldCity')} value={city} onChange={(e) => setCity(e.target.value)} fullWidth />
            <TextField label={t('plt.fieldProvince')} value={province} onChange={(e) => setProvince(e.target.value)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label={t('plt.fieldPostalCode')} value={postalCode} onChange={(e) => setPostalCode(e.target.value)} fullWidth />
            <TextField label={t('plt.fieldPhone')} value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} fullWidth />
          </Stack>
          <TextField label={t('plt.fieldContactEmail')} type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} fullWidth />
          <AddressSchemeFields street={addrStreet} block={addrBlock} onStreet={setAddrStreet} onBlock={setAddrBlock} />

          <Button
            size="small"
            onClick={() => setShowMore((s) => !s)}
            startIcon={showMore ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('plt.moreBtn')}
          </Button>
          {showMore ? (
            <Stack spacing={2.5}>
              <Typography variant="overline" color="text.secondary">{t('plt.advancedSection')}</Typography>
              <TextField label={t('plt.fieldClusterName')} value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
              <TextField
                select
                label={t('plt.devField')}
                value={developerId}
                onChange={(e) => setDeveloperId(e.target.value)}
                helperText={t('plt.devMoveHelper')}
                fullWidth
              >
                {developers.map((d) => (<MenuItem key={d.id} value={d.id} disabled={isLegacyCompany(d.id)}>
                  {isLegacyCompany(d.id) ? t('plt.legacyName') : d.name}
                </MenuItem>))}
              </TextField>
            </Stack>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button onClick={save} variant="contained" disabled={!canSave || busy}>{busy ? t('common.saving') : t('common.save')}</Button>
      </DialogActions>
    </Dialog>
  );
}
